import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const ResetPassword = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setTokenValid(false);
      toast.error(t('toast.invalidResetLink'), { autoClose: 1000 });
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('toast.passwordsDontMatch'), { autoClose: 1000 });
      return;
    }

    if (password.length < 6) {
      toast.error(t('toast.passwordTooShort'), { autoClose: 1000 });
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/reset-password', {
        token,
        new_password: password,
      });
      
      toast.success(t('toast.passwordResetSuccess'), { autoClose: 1000 });
      navigate('/login');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('toast.resetLinkExpired'), { autoClose: 1000 });
        setTokenValid(false);
      } else {
        toast.error(t('toast.passwordResetError'), { autoClose: 1000 });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <>
        <SEO
          title={t('auth.reset.invalidTitle') || 'Invalid Reset Link'}
          description={t('auth.reset.invalidDesc') || 'Password reset link is invalid or expired.'}
          noindex={true}
        />

        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-red-50 rounded-lg p-8">
              <svg
                className="mx-auto h-12 w-12 text-red-600 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('auth.reset.invalidTitle')}
              </h2>
              <p className="text-gray-600 mb-6">
                {t('auth.reset.invalidDesc')}
              </p>
              <Link
                to="/forgot-password"
                className="inline-block px-6 py-3 bg-racing-red text-white rounded-lg hover:bg-red-700 transition font-semibold"
              >
                {t('auth.reset.requestNewLink')}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t('auth.reset.seoTitle') || 'Reset Password - Create New Password'}
        description={t('auth.reset.seoDescription') || 'Create a new password for your Meller Racing account.'}
        noindex={true}
      />

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-center text-3xl font-display font-bold text-gray-900">
              {t('auth.reset.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.reset.subtitle')}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.reset.passwordLabel')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-racing-red focus:border-transparent"
                  placeholder={t('auth.reset.passwordPlaceholder')}
                  minLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('auth.reset.passwordHint')}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.reset.confirmPasswordLabel')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-racing-red focus:border-transparent"
                  placeholder={t('auth.reset.confirmPlaceholder')}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-racing-red text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
            >
              {loading ? (t('auth.reset.submitting') || 'Resetting Password...') : t('auth.reset.submit')}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-racing-red"
              >
                {t('auth.reset.backToLogin')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
